// src/components/account/Navbar.jsx
import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  Divider,
  Chip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { getUser, clearAuth } from '../../lib/auth';
import { api } from '../../lib/api';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import accountNavbarI18n from '../../i18n/components/account/navbar.i18n';

export default function AccountNavbar() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const user = getUser();
  const isMobile = useMediaQuery('(max-width:600px)');

  const { t, register } = useI18n();
  React.useEffect(() => { register('accountNavbar', accountNavbarI18n); }, [register]);

  const LINKS = React.useMemo(() => ([
    { to: '/mes-envois',         label: t('accountNavbar.links.transactions'), icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
    { to: '/mes-envois/compte',  label: t('accountNavbar.links.account'),      icon: <PersonOutlineIcon fontSize="small" /> },
  ]), [t]);

  const logout = async () => {
    try {
      const refresh = localStorage.getItem('refresh');
      const headers = {};
      const cartId = localStorage.getItem('cartId') || sessionStorage.getItem('cartId');
      if (cartId) headers['X-Cart-Id'] = cartId;

      await api.post('/auth/logout', { refresh }, { headers });
    } catch (e) {
      // non bloquant
    } finally {
      clearAuth();
      nav('/services', { replace: true });
    }
  };

  const isActive = (to) => {
    if (to === '/mes-envois') {
      if (pathname === '/mes-envois') return true;
      return /^\/mes-envois\/(?!compte)(.*)$/.test(pathname);
    }
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        color: 'text.primary',
        border: '1px solid #eee',
        borderRadius: 2,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 56, gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              overflowX: { xs: 'auto', sm: 'visible' },
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {LINKS.map(link => {
              const active = isActive(link.to);
              return (
                <Button
                  key={link.to}
                  component={NavLink}
                  to={link.to}
                  startIcon={link.icon}
                  disableRipple
                  sx={{
                    px: { xs: 1.25, sm: 1.5 },
                    py: { xs: 0.6, sm: 0.75 },
                    minWidth: { xs: 'auto', sm: 64 },
                    textTransform: 'none',
                    fontWeight: active ? 800 : 600,
                    fontSize: { xs: 14, sm: 15 },
                    color: active ? '#0466c8' : 'text.primary',
                    bgcolor: active ? 'rgba(11,95,255,0.08)' : 'transparent',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 0.75 } },
                    '&:hover': { bgcolor: 'rgba(11,95,255,0.12)' },
                    position: 'relative',
                    '&::after': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: 8,
                          right: 8,
                          bottom: { xs: -7, sm: -9 },
                          height: 3,
                          borderRadius: 3,
                          backgroundColor: '#0466c8',
                        }
                      : {},
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!isMobile && (
              <Chip
                label={user?.email || '—'}
                size="small"
                sx={{ fontWeight: 700, bgcolor: 'rgba(11,95,255,0.08)', color: '#0466c8' }}
              />
            )}

            {isMobile ? (
              <IconButton onClick={logout} aria-label={t('accountNavbar.logoutAria')} sx={{ color: '#0466c8' }}>
                <LogoutIcon />
              </IconButton>
            ) : (
              <Button
                onClick={logout}
                startIcon={<LogoutIcon />}
                variant="text"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#0466c8',
                  '&:hover': { backgroundColor: 'rgba(11,95,255,0.08)' },
                }}
              >
                {t('accountNavbar.logout')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
      <Divider />
    </AppBar>
  );
}